import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { 
  X, 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  Package, 
  ShoppingCart,
  BarChart3,
  Calendar,
  ChevronDown
} from "lucide-react";
import { StockDualValue } from "@/components/Stock/StockDualValue";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Line, ComposedChart } from 'recharts';
import { BACKEND_URL as __BACKEND_URL__ } from "../../lib/backend-url";

const API = `${__BACKEND_URL__}/api`;

// Period options
const PERIOD_OPTIONS = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "15", label: "Últimos 15 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "60", label: "Últimos 60 dias" },
  { value: "90", label: "Últimos 90 dias" },
  { value: "custom", label: "Personalizado" },
];

export const ProductAnalysis = ({ 
  selectedProduct,
  onClose,
  selectedStoreIds = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [productTicket, setProductTicket] = useState(null);
  const [productMarkup, setProductMarkup] = useState(null);
  const [productStock, setProductStock] = useState(null);
  const [basketAnalysis, setBasketAnalysis] = useState(null);
  const [loadingBasket, setLoadingBasket] = useState(false);
  
  // Daily sales state
  const [dailySalesData, setDailySalesData] = useState(null);
  const [loadingDailySales, setLoadingDailySales] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  useEffect(() => {
    if (selectedProduct) {
      loadProductData();
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedProduct && selectedPeriod !== "custom") {
      loadDailySales();
    }
  }, [selectedProduct, selectedPeriod, selectedStoreIds]);

  const loadProductData = async () => {
    if (!selectedProduct) return;
    
    setLoading(true);
    setLoadingBasket(true);
    
    try {
      const params = new URLSearchParams();
      if (selectedStoreIds.length === 1) {
        params.append("store_id", selectedStoreIds[0]);
      } else if (selectedStoreIds.length > 1) {
        params.append("store_ids", selectedStoreIds.join(","));
      }
      
      // Load ticket data
      const ticketResponse = await axios.get(
        `${API}/sales/product-ticket/${encodeURIComponent(selectedProduct)}?${params.toString()}`
      );
      setProductTicket(ticketResponse.data);
      
      // If we have EAN, load additional data
      if (ticketResponse.data?.ean) {
        const ean = ticketResponse.data.ean;
        
        // Load markup
        try {
          const markupResponse = await axios.get(`${API}/sales/product-markup/${ean}?${params.toString()}`);
          setProductMarkup(markupResponse.data);
        } catch (e) {
          console.error("Error loading markup:", e);
        }
        
        // Load stock
        try {
          const stockParams = new URLSearchParams();
          if (selectedStoreIds.length === 1) stockParams.append("store_id", selectedStoreIds[0]);
          const stockResponse = await axios.get(`${API}/stock/products/${ean}/sales-analysis?${stockParams.toString()}`);
          setProductStock(stockResponse.data);
        } catch (e) {
          console.error("Error loading stock:", e);
        }
        
        // Load basket analysis
        try {
          const basketParams = new URLSearchParams();
          if (selectedStoreIds.length === 1) basketParams.append("store_id", selectedStoreIds[0]);
          basketParams.append("limit", "5");
          const basketResponse = await axios.get(`${API}/sales/basket-analysis/${ean}?${basketParams.toString()}`);
          setBasketAnalysis(basketResponse.data);
        } catch (e) {
          console.error("Error loading basket:", e);
        }
      }
    } catch (error) {
      console.error("Error loading product data:", error);
    } finally {
      setLoading(false);
      setLoadingBasket(false);
    }
  };

  const loadDailySales = useCallback(async () => {
    if (!selectedProduct) return;
    
    setLoadingDailySales(true);
    try {
      const params = new URLSearchParams();
      
      if (selectedStoreIds.length === 1) {
        params.append("store_id", selectedStoreIds[0]);
      } else if (selectedStoreIds.length > 1) {
        params.append("store_ids", selectedStoreIds.join(","));
      }
      
      if (selectedPeriod === "custom" && customStartDate && customEndDate) {
        params.append("start_date", customStartDate.toISOString().split('T')[0]);
        params.append("end_date", customEndDate.toISOString().split('T')[0]);
      } else {
        params.append("days", selectedPeriod);
      }
      
      const response = await axios.get(
        `${API}/sales/product-daily/${encodeURIComponent(selectedProduct)}?${params.toString()}`
      );
      setDailySalesData(response.data);
    } catch (error) {
      console.error("Error loading daily sales:", error);
    } finally {
      setLoadingDailySales(false);
    }
  }, [selectedProduct, selectedPeriod, customStartDate, customEndDate, selectedStoreIds]);

  const handlePeriodChange = (value) => {
    setSelectedPeriod(value);
    if (value === "custom") {
      setShowCustomDatePicker(true);
    } else {
      setShowCustomDatePicker(false);
    }
  };

  const applyCustomDates = () => {
    if (customStartDate && customEndDate) {
      loadDailySales();
      setShowCustomDatePicker(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { 
      style: 'currency', 
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(value);
  };

  if (!selectedProduct) return null;

  return (
    <Card className="mt-4 border-purple-200 bg-gradient-to-r from-purple-50 to-white" data-testid="product-analysis-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-600" />
            Análise: <span className="text-purple-700">{selectedProduct}</span>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            data-testid="close-analysis-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-slate-600">Carregando análise...</span>
          </div>
        ) : (
          <>
            {/* Summary Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Ticket Info Card */}
              {productTicket && (
                <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                  <h5 className="text-sm font-semibold text-slate-700 mb-2">Ticket Médio</h5>
                  <p className="text-2xl font-bold text-amber-600">
                    R$ {productTicket.avg_unit_price?.toFixed(2) || '0,00'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {productTicket.total_quantity?.toFixed(0) || 0} unidades vendidas
                  </p>
                  <p className="text-xs text-slate-500">
                    {productTicket.total_orders || 0} pedidos
                  </p>
                </div>
              )}

              {/* Markup Card */}
              {productMarkup && (
                <div className="p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200">
                  <h5 className="text-sm font-semibold text-slate-700 mb-2">Markup do Produto</h5>
                  <p className={`text-2xl font-bold ${productMarkup.markup_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {productMarkup.markup_percent?.toFixed(1) || 0}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Custo: R$ {productMarkup.cost_price?.toFixed(2) || '0,00'}
                  </p>
                  <p className="text-xs text-slate-500">
                    Venda: R$ {productMarkup.sale_price?.toFixed(2) || '0,00'}
                  </p>
                </div>
              )}

              {/* Stock Card */}
              {productStock && (
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                  <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4 text-blue-600" />
                    Estoque e Ritmo
                  </h5>
                  <StockDualValue product={productStock} size="lg" showLabel />
                  <p className="text-xs text-slate-500 mt-1">
                    Média diária: {productStock.avg_daily_sales?.toFixed(1) || 0} un/dia
                  </p>
                  <p className="text-xs text-slate-500">
                    Duração: ~{productStock.days_of_stock?.toFixed(0) || 0} dias
                  </p>
                </div>
              )}

              {/* Basket Analysis Card */}
              <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-200">
                <h5 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-orange-600" />
                  Produtos Comprados Juntos
                </h5>
                {loadingBasket ? (
                  <div className="flex items-center gap-2 text-orange-600 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Analisando...</span>
                  </div>
                ) : basketAnalysis?.has_data && basketAnalysis?.basket_products?.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-xs text-slate-500 mb-2">
                      Top 5 ({basketAnalysis.total_orders} pedidos)
                    </p>
                    {basketAnalysis.basket_products.map((bp, idx) => (
                      <div key={bp.ean} className="flex items-center justify-between p-1.5 bg-white rounded hover:bg-orange-50 transition-colors">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            idx === 0 ? 'bg-orange-500 text-white' : 
                            idx === 1 ? 'bg-orange-400 text-white' : 
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {idx + 1}
                          </span>
                          <p className="text-xs text-slate-700 truncate" title={bp.produto}>
                            {bp.produto?.substring(0, 20)}...
                          </p>
                        </div>
                        <span className="text-xs font-bold text-orange-600 ml-1">
                          {bp.affinity_score}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Sem dados disponíveis</p>
                )}
              </div>
            </div>

            {/* Daily Sales Chart Card */}
            <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-white" data-testid="daily-sales-card">
              <CardHeader className="pb-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    Vendas Diárias
                  </CardTitle>
                  
                  {/* Period Selector */}
                  <div className="flex items-center gap-2">
                    <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
                      <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="period-selector">
                        <Calendar className="w-4 h-4 mr-2 text-slate-500" />
                        <SelectValue placeholder="Selecione o período" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIOD_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Custom Date Picker */}
                    {selectedPeriod === "custom" && (
                      <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm" className="h-9">
                            {customStartDate && customEndDate 
                              ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                              : "Selecionar datas"
                            }
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-4" align="end">
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-slate-600">Data Inicial</Label>
                                <CalendarComponent
                                  mode="single"
                                  selected={customStartDate}
                                  onSelect={setCustomStartDate}
                                  className="rounded-md border"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-slate-600">Data Final</Label>
                                <CalendarComponent
                                  mode="single"
                                  selected={customEndDate}
                                  onSelect={setCustomEndDate}
                                  className="rounded-md border"
                                />
                              </div>
                            </div>
                            <Button 
                              onClick={applyCustomDates} 
                              className="w-full bg-indigo-600 hover:bg-indigo-700"
                              disabled={!customStartDate || !customEndDate}
                            >
                              Aplicar
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingDailySales ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    <span className="ml-2 text-slate-600">Carregando vendas diárias...</span>
                  </div>
                ) : dailySalesData ? (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-white rounded-lg border border-indigo-100">
                        <p className="text-xs text-slate-500">Total Quantidade</p>
                        <p className="text-lg font-bold text-indigo-600">
                          {dailySalesData.total_quantity?.toFixed(0) || 0} un
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-indigo-100">
                        <p className="text-xs text-slate-500">Total Faturamento</p>
                        <p className="text-lg font-bold text-emerald-600">
                          {formatCurrency(dailySalesData.total_revenue || 0)}
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-indigo-100">
                        <p className="text-xs text-slate-500">Média Diária (qty)</p>
                        <p className="text-lg font-bold text-blue-600">
                          {dailySalesData.avg_daily_quantity?.toFixed(1) || 0} un
                        </p>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-indigo-100">
                        <p className="text-xs text-slate-500">Dias com Vendas</p>
                        <p className="text-lg font-bold text-amber-600">
                          {dailySalesData.days_with_sales || 0}/{dailySalesData.total_days || 0}
                        </p>
                      </div>
                    </div>
                    
                    {/* Chart */}
                    {dailySalesData.daily_sales?.length > 0 && (
                      <div className="h-[280px] mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart 
                            data={dailySalesData.daily_sales}
                            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={formatDate}
                              tick={{ fontSize: 10, fill: '#666' }}
                              angle={-45}
                              textAnchor="end"
                              height={50}
                              interval={Math.floor(dailySalesData.daily_sales.length / 10)}
                            />
                            <YAxis 
                              yAxisId="left"
                              tick={{ fontSize: 10, fill: '#666' }}
                              tickFormatter={(v) => `${v}`}
                              width={40}
                            />
                            <YAxis 
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 10, fill: '#666' }}
                              tickFormatter={(v) => `R$${v}`}
                              width={60}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: 'white', 
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                fontSize: '12px'
                              }}
                              formatter={(value, name) => {
                                if (name === 'quantity') return [`${value.toFixed(1)} un`, 'Quantidade'];
                                if (name === 'revenue') return [formatCurrency(value), 'Faturamento'];
                                return [value, name];
                              }}
                              labelFormatter={(label) => {
                                const date = new Date(label);
                                return date.toLocaleDateString('pt-BR', { 
                                  weekday: 'long', 
                                  day: '2-digit', 
                                  month: 'long' 
                                });
                              }}
                            />
                            <Bar 
                              yAxisId="left"
                              dataKey="quantity" 
                              fill="#6366f1" 
                              radius={[4, 4, 0, 0]}
                              name="quantity"
                            />
                            <Line 
                              yAxisId="right"
                              type="monotone" 
                              dataKey="revenue" 
                              stroke="#10b981" 
                              strokeWidth={2}
                              dot={false}
                              name="revenue"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    
                    {/* Period info */}
                    <p className="text-xs text-slate-500 text-right">
                      Período: {dailySalesData.period?.start && formatDate(dailySalesData.period.start)} a {dailySalesData.period?.end && formatDate(dailySalesData.period.end)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Selecione um período para visualizar as vendas diárias
                  </p>
                )}
              </CardContent>
            </Card>
          </>
        )}
        
        <p className="text-xs text-slate-500 italic mt-4">
          Dados baseados no período e lojas selecionadas
        </p>
      </CardContent>
    </Card>
  );
};

export default ProductAnalysis;
